import { Box, Card, CardContent, Typography } from "@mui/material";
import type { JSX } from "react";

import type { Tone } from "./securityFormat.js";

/**
 * A labelled horizontal-bar breakdown (by source / by outcome) for the
 * Security console. Bars scale to the largest row in the set. Extracted from
 * `SecurityPanel.tsx`.
 */
export function BreakdownCard({
  title,
  rows,
  empty,
}: {
  title: string;
  rows: { label: string; n: number; tone?: Tone }[];
  empty: string;
}): JSX.Element {
  const max = rows.reduce((m, r) => Math.max(m, r.n), 0) || 1;
  return (
    <Card>
      <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
        <Typography
          sx={{
            mb: 1,
            fontSize: 12,
            fontWeight: 500,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            color: "text.disabled",
          }}
        >
          {title}
        </Typography>
        {rows.length === 0 ? (
          <Typography sx={{ fontSize: 14, color: "text.disabled" }}>{empty}</Typography>
        ) : (
          <Box sx={{ display: "grid", gap: 0.75 }}>
            {rows.map((r) => (
              <Box key={r.label} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  component="span"
                  sx={{
                    width: 96,
                    flexShrink: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    fontSize: 12,
                    color: "text.disabled",
                  }}
                >
                  {r.label}
                </Box>
                <Box
                  sx={{
                    height: 8,
                    flex: 1,
                    overflow: "hidden",
                    borderRadius: 999,
                    bgcolor: "surfaceSoft",
                  }}
                >
                  <Box
                    sx={{
                      height: "100%",
                      borderRadius: 999,
                      bgcolor:
                        r.tone === "good"
                          ? "success.main"
                          : r.tone === "danger"
                            ? "error.main"
                            : "primary.main",
                      width: `${Math.round((r.n / max) * 100)}%`,
                    }}
                  />
                </Box>
                <Box
                  component="span"
                  sx={{ width: 32, flexShrink: 0, textAlign: "right", fontSize: 12, fontVariantNumeric: "tabular-nums" }}
                >
                  {r.n}
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
